/**
 * Leadership's BTI tab (5D, DEC-49, criterion F6): Wave 1 and Wave 2 in one layout. Each wave is a structured section
 * (question, answer, state chip, meaning, source), then BTI's areas, each with how much is in place and its
 * requirements; each requirement opens to where firms stand on it today.
 *
 * Only what the reader may see: BTI's Wave 1 verdicts are shared content (everyone); the network's per-requirement
 * counts, National IT's positions and every firm's position by name are for National IT and the Board (the API sends
 * them only to them); a firm member sees their own firm's position only. BTI's evaluator comments are never here
 * (National IT only, on the National view).
 *
 * Where a firm's position comes from: its current status on the baseline control each requirement maps to
 * (btiFirmItems: CDW 2026 result, National IT's accepted review, or the firm's own rating). A control with no 2026
 * rating shows the March 2025 survey answer when the firm has one (DEC-72), and otherwise reads "No 2026 rating yet".
 * The bar is the CDW 2026 assessment when the firm has one, and the survey answer when it does not. BTI's July 2025
 * review stays beside the network's own all-firms state.
 */
import {
  BTI_DOMAINS,
  BTI_FIRM_STATUS,
  BTI_POSITION,
  BTI_VERDICT,
  BTI_WAVE1_EVALUATION,
  BTI_WAVE2_DUE,
  BTI_WAVE2_ITEMS,
  btiAreaCounts,
  btiPositionCounts,
  btiVerdictCounts,
  NETWORK_REQUIREMENT,
  networkRequirementState,
  ownCount,
  requirementBar,
  firmSourceText,
  longDate,
  networkSourceText,
  SOURCE,
  stateOf,
  type BtiFirmPosition,
  type BtiFirmStatus,
  type BtiPosition,
  type BtiVerdict,
  type LeadershipFirm,
  type LeadershipNetwork,
} from '@grc/shared/vocabulary';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { InPlaceKey } from '../charts';
import { AnswerSection } from '../components';
import { itemName, PositionDetail, type Measured } from './Bti';

const pct = (v: number | null | undefined) => (v === null || v === undefined ? 'No results yet' : `${Math.round(v)}%`);
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const share = (n: number, of: number) => (of ? Math.round((n / of) * 100) : 0);
const due = longDate(BTI_WAVE2_DUE);

/** One requirement, either wave: BTI's verdict only for Wave 1 (BTI has not rated Wave 2). */
interface Requirement {
  no: number;
  domain: string;
  title: string;
  controlId: string;
  verdict?: BtiVerdict;
}

const REQUIREMENTS: Record<1 | 2, Requirement[]> = {
  1: BTI_WAVE1_EVALUATION.items.map((i) => ({ no: i.no, domain: i.domain, title: i.title, controlId: i.controlId, verdict: i.verdict })),
  2: BTI_WAVE2_ITEMS.map((i) => ({ no: i.no, domain: i.domain, title: i.title, controlId: i.controlId })),
};

const STATUS_ORDER: BtiFirmStatus[] = ['fully', 'partly', 'not_yet', 'out_of_scope', 'not_assessed'];

/** Where a position comes from, after the firm code; a CDW 2026 assessment needs no mark. */
const sourceMark = (s: BtiFirmPosition['source']) =>
  s === 'self' ? ' (own rating)' : s === 'reviewed' ? ' (National IT review)' : s === 'survey' ? ' (March 2025 survey)' : '';

/** Fully, partly and not yet in place as one bar: teal, hatched blue, the track (the colours of every chart). */
function ThreePart({ fully, partly, notYet, label }: { fully: number; partly: number; notYet: number; label: string }) {
  const all = fully + partly + notYet;
  const w = (v: number) => `${all ? (v / all) * 100 : 0}%`;
  return (
    <span className="lead-gap-bar bti-req-bar" role="img" aria-label={label}>
      {fully > 0 && <span className="seg in-place-now" style={{ width: w(fully) }} />}
      {partly > 0 && <span className="seg partly" style={{ width: w(partly) }} />}
      {notYet > 0 && <span className="seg in-place" style={{ width: w(notYet) }} />}
    </span>
  );
}

/**
 * One requirement (5E, DEC-58): its row, and a labelled button that opens where firms stand on it. The button is the
 * control (at least 24 by 24 px, keyboard operable, aria-expanded); a click anywhere else on the row does the same for
 * a mouse. What opens stays in the page while closed (hidden), so it is the same content either way.
 */
function RequirementRow({ wave, no, title, member, row, body }: { wave: 1 | 2; no: number; title: string; member: boolean; row: ReactNode; body: ReactNode }) {
  const [open, setOpen] = useState(false);
  const bodyId = `bti-req-${wave}-${no}`;
  const bodyRef = useRef<HTMLDivElement>(null);
  // CP-05 (5E pre-gate): a closed row is hidden="until-found", so find in page still finds a firm's name in it; the
  // browser fires beforematch before it shows the match, and the row opens with it (the button then says so).
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    if (open) el.removeAttribute('hidden');
    else el.setAttribute('hidden', 'until-found');
    const reveal = () => setOpen(true);
    el.addEventListener('beforematch', reveal);
    return () => el.removeEventListener('beforematch', reveal);
  }, [open]);
  // DEC-60: a firm member's row already shows all they may see (their own status chip), so it has no toggle and nothing
  // to open; National IT and the Board keep "Show firms".
  if (member)
    return (
      <li className="bti-req">
        <div className="bti-req-row bti-req-row-static">{row}</div>
      </li>
    );
  return (
    <li className={open ? 'bti-req open' : 'bti-req'}>
      <div
        className="bti-req-row"
        onClick={(e) => {
          // CP-03 (5E pre-gate): selecting text in the row (to copy a title) does not open or close it.
          if (window.getSelection()?.toString()) return;
          if (!(e.target as HTMLElement).closest('button, a')) setOpen((o) => !o);
        }}
      >
        {row}
        <button type="button" className="bti-req-toggle" aria-expanded={open} aria-controls={bodyId} onClick={() => setOpen((o) => !o)}>
          <span className="bti-req-chevron" aria-hidden="true" />
          {open ? 'Hide' : 'Show'} firms
          <span className="sr-only">: {itemName(wave, no)}, {title}</span>
        </button>
      </div>
      <div className="bti-req-body" id={bodyId} ref={bodyRef}>
        {body}
      </div>
    </li>
  );
}

/** "National IT’s position: 1 of 25 met; 24 not yet recorded." The same form for both waves; no bare zero. */
function positionLine(positions: readonly BtiPosition[], wave: 1 | 2): string {
  const c = btiPositionCounts(positions, wave);
  const size = REQUIREMENTS[wave].length;
  const parts = [`${c.met} of ${size} met`, ...(c.partly ? [`${c.partly} partly met`] : []), ...(c.not_yet ? [`${c.not_yet} not yet met`] : [])];
  return `National IT’s position: ${parts.join(', ')}${c.not_set ? `; ${c.not_set} not yet recorded` : ''}.`;
}

/** The firms behind one requirement, grouped by where they stand today. */
function FirmPositions({ firms, selected }: { firms: BtiFirmPosition[]; selected: string | null }) {
  return (
    <ul className="plain bti-firm-groups">
      {STATUS_ORDER.filter((s) => firms.some((f) => f.status === s)).map((s) => (
        <li key={s} className="bti-firm-group">
          <span className={`chip tone-${BTI_FIRM_STATUS[s].tone}`}>{BTI_FIRM_STATUS[s].label}</span>{' '}
          {firms
            .filter((f) => f.status === s)
            .map((f, i, all) => (
              <span key={f.firmCode} className={f.firmCode === selected ? 'bti-firm you' : 'bti-firm'}>
                {f.firmCode}
                {sourceMark(f.source)}
                {i < all.length - 1 ? ', ' : ''}
              </span>
            ))}
        </li>
      ))}
    </ul>
  );
}

function WaveSection({ wave, n, firm, member }: { wave: 1 | 2; n: LeadershipNetwork; firm: LeadershipFirm | null; member: boolean }) {
  const items = REQUIREMENTS[wave];
  const id = wave === 1 ? 'bti-h' : 'bti2-h';
  const size = items.length;
  // Network counts and every firm by name for National and the Board; a firm member's own firm only.
  const measured: Measured[] | undefined = member ? undefined : wave === 1 ? n.btiWave1 : n.btiWave2;
  const byFirm = member ? undefined : n.btiByFirm?.filter((r) => r.wave === wave);
  const own = member && firm ? firm.bti?.filter((r) => r.wave === wave) : undefined;
  const positions = member ? undefined : n.btiPositions?.filter((p) => p.wave === wave);
  const value = member && firm ? firm.headline.bti[wave === 1 ? 'wave1' : 'wave2'].value : wave === 1 ? n.btiWave1Median : n.btiWave2Median;
  const who = member && firm ? firm.firmName : 'the typical firm';
  const today = value === null ? 'there is no figure for today yet' : `today ${who} has ${pct(value)} in place`;
  const sat = btiVerdictCounts().satisfied;
  const meaning =
    wave === 1
      ? `BTI marked ${sat} of the ${size} requirements satisfied in its July 2025 review; ${today}.`
      : `The ${size} requirements are due ${due}, and BTI rates them after that; ${today}.`;
  const where = member && firm ? firmSourceText(firm) : networkSourceText(n);
  const source = `${wave === 1 ? SOURCE.btiReview : SOURCE.btiWave2}; ${where}; ${SOURCE.mapping}`;
  const measureOf = (r: Requirement) => measured?.find((m) => m.controlId === r.controlId);
  const ownOf = (r: Requirement) => own?.find((x) => x.itemNo === r.no);
  const areas = btiAreaCounts(wave, own ? own.map(ownCount) : (measured ?? []));
  return (
    // 5E (DEC-58): the shared AnswerSection, the one question style on every page.
    <AnswerSection
      id={id}
      className="bti-wave"
      wave={wave}
      question={member && firm ? `Where is ${firm.firmName} on Wave ${wave}?` : `Where is the network on Wave ${wave}?`}
      answer={pct(value)}
      chip={stateOf(value)}
      meaning={meaning}
      note={positions && positions.length > 0 ? <p className="small bti-position-count">{positionLine(positions, wave)}</p> : undefined}
      source={source}
    >
      <InPlaceKey />
      <p className="legend small bti-wave-key">
        {wave === 1 ? (member ? '“BTI’s review” on each requirement is from BTI’s July 2025 review.' : '“BTI’s review” is BTI’s July 2025 review, beside the network’s own state.') : `BTI has not rated Wave 2 yet; it rates it after the ${due} submission.`}{' '}
        {member
          ? '“Your firm today” is where your firm stands now; other firms are not shown. It is the CDW 2026 assessment when your firm has one, and the March 2025 survey when there is no 2026 rating.'
          : 'A firm’s place on the bar is its CDW 2026 assessment when it has one. A firm with no 2026 assessment shows its March 2025 survey answer. The network is satisfied only when every firm with a position has it in place, partly satisfied when some do and some do not, and not met when none do.'}
        {positions && positions.length > 0 && <> The dashed chip is National IT&rsquo;s position ({Object.values(BTI_POSITION).map((v) => v.label).join(', ')}), beside BTI&rsquo;s review, never in place of it.</>}
      </p>
      {BTI_DOMAINS.filter((d) => items.some((i) => i.domain === d)).map((d) => {
        const inArea = items.filter((i) => i.domain === d);
        // The area: the firm-requirements fully, partly and not yet in place, summed over its requirements (the PDF's rule too).
        // National and the Board sum the same bars the requirements draw (CDW 2026, or the March 2025 survey when there is no 2026 rating).
        const fromBars = byFirm
          ? inArea.reduce(
              (a, r) => {
                const b = requirementBar(byFirm.find((x) => x.itemNo === r.no)?.firms ?? []);
                return { fully: a.fully + b.fully, partly: a.partly + b.partly, notYet: a.notYet + b.notYet };
              },
              { fully: 0, partly: 0, notYet: 0 },
            )
          : null;
        const sum = fromBars ?? areas.find((a) => a.domain === d) ?? { fully: 0, partly: 0, notYet: 0 };
        const rated = sum.fully + sum.partly + sum.notYet;
        const verdicts = wave === 1 ? btiVerdictCounts(BTI_WAVE1_EVALUATION.items.filter((i) => i.domain === d)) : null;
        const areaText = !rated
          ? own ? 'No 2026 rating yet' : 'No CDW results yet'
          : own
            ? `${sum.fully + sum.partly} of ${rated} in place (${sum.fully} fully)`
            : `${share(sum.fully + sum.partly, rated)}% in place across firms (${share(sum.fully, rated)}% fully)`;
        return (
          <div key={d} className="bti-area">
            <h4 className="bti-area-name">
              {d} <span className="muted small">{plural(inArea.length, 'requirement')}</span>
            </h4>
            <div className="bti-area-measure">
              {rated > 0 && <ThreePart fully={sum.fully} partly={sum.partly} notYet={sum.notYet} label={`${d}, Wave ${wave}: ${areaText}`} />}
              <span className="small">
                <strong>{areaText}</strong>
                {verdicts && <span className="muted">. BTI rated {verdicts.satisfied} of {inArea.length} satisfied</span>}
              </span>
            </div>
            <ol className="plain bti-reqs">
              {inArea.map((r) => {
                const m = measureOf(r);
                const mine = ownOf(r);
                const firms = byFirm?.find((x) => x.itemNo === r.no)?.firms;
                const p = positions?.find((x) => x.itemNo === r.no);
                const fully = m ? m.inPlace - m.partly : 0;
                return (
                  <RequirementRow key={r.no} wave={wave} no={r.no} member={member} title={r.title}
                    row={
                      <>
                        <span className="bti-req-title">
                          <span className="muted small">{r.no}. </span>
                          {r.title}
                        </span>
                        <span className="bti-req-chips">
                          {/* DEC-60 follow-up (5E NEXT PASS 3): on a member's row both chips carry a visible label, so BTI's
                              rating and the firm's own position are never read as one. */}
                          {r.verdict && (
                            <span className="bti-chip-item">
                              <span className="bti-chip-label">BTI&rsquo;s review</span>
                              <span className={`chip tone-${BTI_VERDICT[r.verdict].tone}`}>{BTI_VERDICT[r.verdict].label}</span>
                            </span>
                          )}
                          {!own && firms && (() => {
                            const state = networkRequirementState(firms);
                            if (!state) return null;
                            return (
                              <span className="bti-chip-item">
                                <span className="bti-chip-label">Network</span>
                                <span className={`chip tone-${NETWORK_REQUIREMENT[state].tone}`}>{NETWORK_REQUIREMENT[state].label}</span>
                              </span>
                            );
                          })()}
                          {p && (
                            <span className={`chip bti-national tone-${BTI_POSITION[p.position].tone}`}>
                              National IT: {BTI_POSITION[p.position].label}
                              <span className="sr-only">, National IT&rsquo;s position on {itemName(wave, r.no)}</span>
                            </span>
                          )}
                          {own && !r.verdict && <span className="bti-chip-placeholder" aria-hidden="true" />}
                          {own && (
                            <span className="bti-chip-item">
                              <span className="bti-chip-label">Your firm today</span>
                              <span className={`chip tone-${BTI_FIRM_STATUS[mine?.status ?? 'not_assessed'].tone}`}>{BTI_FIRM_STATUS[mine?.status ?? 'not_assessed'].label}</span>
                              {/* The legend's marks, here too: where the firm's position comes from when it is not CDW's result. */}
                              {sourceMark(mine?.source ?? null) && <span className="muted small bti-source-mark">{sourceMark(mine?.source ?? null).trim()}</span>}
                            </span>
                          )}
                        </span>
                        {!own &&
                          (firms
                            ? (() => {
                                const b = requirementBar(firms);
                                return b.rated > 0 ? (
                                  <span className="bti-req-measure">
                                    <ThreePart fully={b.fully} partly={b.partly} notYet={b.notYet} label={`${r.title}: in place at ${b.fully + b.partly} (fully at ${b.fully}, partly at ${b.partly}), not yet at ${b.notYet}, of ${b.rated} firms. A firm's place is its CDW 2026 assessment when it has one, otherwise its March 2025 survey answer.`} />
                                    <span className="small bti-now">
                                      In place at {b.fully + b.partly} of {b.rated} firms ({b.fully} fully)
                                    </span>
                                  </span>
                                ) : (
                                  <span className="muted small">No CDW results yet</span>
                                );
                              })()
                            : m && m.rated > 0 ? (
                            <span className="bti-req-measure">
                              <ThreePart fully={fully} partly={m.partly} notYet={m.rated - m.inPlace} label={`${r.title}: in place at ${m.inPlace} (fully at ${fully}, partly at ${m.partly}), not yet at ${m.rated - m.inPlace}, of ${m.rated} firms. A firm's place is its CDW 2026 assessment when it has one.`} />
                              <span className="small bti-now">
                                In place at {m.inPlace} of {m.rated} firms ({fully} fully)
                              </span>
                            </span>
                          ) : (
                            <span className="muted small">No CDW results yet</span>
                          ))}
                      </>
                    }
                    body={
                      <>
                        {p && (
                          <PositionDetail
                            p={p}
                            footnote={r.verdict ? <>BTI&rsquo;s rating is unchanged: {BTI_VERDICT[r.verdict].label.toLowerCase()}.</> : <>BTI evaluates Wave 2 after the {due} submission.</>}
                          />
                        )}
                        {own ? (
                          <>
                            <p className="small">
                              <span className={`chip tone-${BTI_FIRM_STATUS[mine?.status ?? 'not_assessed'].tone}`}>{BTI_FIRM_STATUS[mine?.status ?? 'not_assessed'].label}</span> {firm!.firmName}
                              {sourceMark(mine?.source ?? null)}
                            </p>
                            <p className="muted small">Other firms are not shown.</p>
                          </>
                        ) : firms ? (
                          <FirmPositions firms={firms} selected={firm?.firmCode ?? null} />
                        ) : (
                          <p className="muted small">Where each firm stands is not available here.</p>
                        )}
                      </>
                    }
                  />
                );
              })}
            </ol>
          </div>
        );
      })}
      <p className="legend small">Firm positions: a firm&rsquo;s place is its CDW 2026 assessment when it has one. A firm with no 2026 assessment shows its March 2025 survey answer, marked (March 2025 survey). An own rating or a National IT review is marked. No 2026 rating yet: the firm has neither a 2026 rating nor a March 2025 survey answer for that control.</p>
    </AnswerSection>
  );
}

/** Both waves, Wave 1 first, in the one layout. */
export function BtiWaves({ n, firm, member }: { n: LeadershipNetwork; firm: LeadershipFirm | null; member: boolean }) {
  return (
    <div className="lead-grid">
      <WaveSection wave={1} n={n} firm={firm} member={member} />
      <WaveSection wave={2} n={n} firm={firm} member={member} />
    </div>
  );
}
